import React, { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Spin } from 'antd';
import type { MenuProps } from 'antd';
import {
    TreeEvergreenIcon,
    SunIcon,
    ArrowsClockwiseIcon,
    CubeIcon,
    CheckSquareIcon,
    CurrencyDollarIcon,
    HeartIcon,
    InfoIcon,
    ScalesIcon,
    DotsThreeIcon,
    ListBulletsIcon,
    SquaresFourIcon,
} from '@phosphor-icons/react';
import { useViewerStore } from '../../../../../store/store';
import { resolveTreeCutScenarioSnapshot } from '../utils/catalogCardSnapshot';
import type { AiDesignsDataSource } from './DashboardAiDesignsSourceBadge';
import {
    formatTreeScenarioCutDetail,
    patchScenarioWithCutCostOption,
    type TreeCutCostOptionApplyPayload,
} from '../../../../../services/treeCartService';
import {
    treeCutDashboardCardKey,
    type DesignEpcCell,
    type EpcDisplayPartner,
    type TreeCutPricingTable,
} from '../utils/pricing';
import { OBD_AI_DESIGNS_EPC_TREE_CUTTING_NESTED_MODAL_Z } from '../oneButtonOverlayZ';
import { DashboardAiDesignsEpcModal } from './DashboardAiDesignsEpcModal';
import type { DashboardAiDesignEpcRow } from './DashboardAiDesignsEpcModal.types';
import {
    buildDashboardLayoutPerfDetailLine,
    DashboardAiCatalogListRow,
    formatDashboardAnnualProductionLine,
    joinDashboardCatalogDetailLines,
} from './DashboardAiCatalogListRow';
import { TreeCutAlternateCutCostsModal } from './TreeCutAlternateCutCostsModal';
import { TreeCuttingDefaultsModal } from './TreeCuttingDefaultsModal';
import {
    DashboardAiRailTreeCuttingMultiTable,
    DashboardAiRailTreeCuttingSingleTable,
    type TreeScenarioTablesProps,
} from './DashboardAiRailTreeCuttingScenarioTables';

export interface DashboardAiRailTreeCuttingPanelProps {
    theme: string;
    /** Design name shown on the tree-cutting toolbar row (swapped with subpanel “Tree cutting” title). */
    designLabel?: string;
    treeScenariosCatalogViewMode: 'table' | 'card';
    setTreeScenariosCatalogViewMode: (mode: 'table' | 'card') => void;
    treeCuttingForLayoutKey: string | null;
    handleReRunTreeScenarios: () => void | Promise<void>;
    reRunningTreeScenarios: boolean;
    sceneViewer: unknown;
    aiModuleFillingScenarios: unknown[] | null | undefined;
    modalTreeData: {
        singleTreeWithWorthIt?: unknown[];
        multiTreeScenarios?: unknown[];
        baseWattage?: number;
        totalModulesBase?: number;
    };
    layoutMetricsByKey: Record<string, any> | undefined;
    effectiveTreeLayoutKey: string;
    selectedRoofCombinationKey: string | null | undefined;
    activeLayoutRoofIds: string[] | undefined;
    activeModule: unknown;
    roofCards: any[];
    renderTreeCutGoodLeapPurchaseOnCard: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        options?: { hidePlaceholderUntilLoaded?: boolean },
    ) => ReactNode;
    renderTreeCutIgsPrimaryOnCard: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        options?: { hidePlaceholderUntilLoaded?: boolean },
    ) => ReactNode;
    renderTreeCutLightReachTotalEpcPaymentOnCard: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        options?: { hidePlaceholderUntilLoaded?: boolean },
    ) => ReactNode;
    solarUtilityData: unknown;
    triggerTreeCutScenarioPartnerPricing: (
        scenario: any,
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
    ) => void | Promise<void>;
    /** Opens the main financing pricing modal for this row (in addition to inline partner cells). */
    onOpenTreeScenarioFinancingPricing?: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        metrics: { systemKw: string; systemSizeKw?: number; estModules: number | undefined; roofCount: number },
    ) => void;
    isTreeCutScenarioSaved: (index: number) => boolean;
    handleSaveTreeCutScenario: (scenario: any, index: number) => void;
    applyingScenarioIndex: number | null | undefined;
    handleUseThisLayout: (index: number, layoutKey: string | null, scenarios: unknown) => void;
    treeScenariosByLayoutKey: Record<string, any> | undefined;
    setTreeCuttingPopupOpen: (open: boolean) => void;
    setTreeCuttingForLayoutKey: (key: string | null) => void;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    getTreeCutPartnerCellState: (
        cardKey: string,
        partner: EpcDisplayPartner,
    ) => { status?: string; errorDetail?: string } | undefined;
    getTreeCutEpcByPartner: (cardKey: string) => Record<EpcDisplayPartner, DesignEpcCell>;
    isTreeCutEpcLoading: (cardKey: string) => boolean;
    ensureTreeCutPricingForCard: (
        cardKey: string,
        scenario: { actualWattage?: number; cutCost?: unknown },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
    ) => Promise<unknown | null>;
    /** Tree-cutting modal: place cut-cost / production chips under the card thumbnail */
    supplementsBelowThumb?: boolean;
}

export function DashboardAiRailTreeCuttingPanel({
    theme,
    designLabel,
    treeScenariosCatalogViewMode,
    setTreeScenariosCatalogViewMode,
    treeCuttingForLayoutKey,
    handleReRunTreeScenarios,
    reRunningTreeScenarios,
    sceneViewer,
    aiModuleFillingScenarios,
    modalTreeData,
    layoutMetricsByKey,
    effectiveTreeLayoutKey,
    selectedRoofCombinationKey,
    activeLayoutRoofIds,
    activeModule,
    roofCards,
    renderTreeCutGoodLeapPurchaseOnCard,
    renderTreeCutIgsPrimaryOnCard,
    renderTreeCutLightReachTotalEpcPaymentOnCard,
    solarUtilityData,
    triggerTreeCutScenarioPartnerPricing,
    onOpenTreeScenarioFinancingPricing,
    isTreeCutScenarioSaved,
    handleSaveTreeCutScenario,
    applyingScenarioIndex,
    handleUseThisLayout,
    treeScenariosByLayoutKey,
    setTreeCuttingPopupOpen,
    setTreeCuttingForLayoutKey,
    setIsDashboardOpen,
    getTreeCutPartnerCellState,
    getTreeCutEpcByPartner,
    ensureTreeCutPricingForCard,
    supplementsBelowThumb = false,
}: DashboardAiRailTreeCuttingPanelProps) {
    const [treeCutDefaultsModalOpen, setTreeCutDefaultsModalOpen] = useState(false);
    const aiDesignsDataSource = useViewerStore(
        (s: any) => (s.aiDesignsDataSource as AiDesignsDataSource | undefined) ?? null,
    );
    const resolveTreeCutThumb = useCallback(
        (scenario: { actualWattage?: number; snapshotUrl?: string; layoutSnapshotUrl?: string; snapshotCaptureError?: string }) =>
            resolveTreeCutScenarioSnapshot(scenario, aiDesignsDataSource),
        [aiDesignsDataSource],
    );
    const [epcModalOpen, setEpcModalOpen] = useState(false);
    const treeEpcMetaRef = useRef<
        Record<string, { scenario: { actualWattage?: number; cutCost?: unknown }; table: TreeCutPricingTable; rowIndex: number }>
    >({});
    const [toolbarOverflowOpen, setToolbarOverflowOpen] = useState(false);

    const toolbarOverflowItems: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'rerun',
                label: 'Re-run tree simulation',
                icon: <ArrowsClockwiseIcon size={16} weight="bold" aria-hidden />,
                disabled: !sceneViewer || reRunningTreeScenarios,
                onClick: () => {
                    if (sceneViewer && !reRunningTreeScenarios) void handleReRunTreeScenarios();
                },
            },
            {
                key: 'defaults',
                label: 'Tree cutting defaults',
                icon: <InfoIcon size={16} weight="duotone" aria-hidden />,
                onClick: () => setTreeCutDefaultsModalOpen(true),
            },
        ],
        [sceneViewer, reRunningTreeScenarios, handleReRunTreeScenarios],
    );
    const [alternateCutModal, setAlternateCutModal] = useState<{
        open: boolean;
        treeIds: string[];
        label: string;
        scenarioIndex: number;
        defaultCutCost: number | null;
        selectedOptionId: string | null;
    }>({ open: false, treeIds: [], label: '', scenarioIndex: -1, defaultCutCost: null, selectedOptionId: null });

    const openAlternateCutCosts = (
        treeIds: string[],
        label: string,
        scenarioIndex: number,
        defaultCutCost: number | null,
        selectedOptionId?: string | null,
    ) => {
        setAlternateCutModal({
            open: true,
            treeIds,
            label,
            scenarioIndex,
            defaultCutCost,
            selectedOptionId: selectedOptionId ?? null,
        });
    };

    const handleApplyCutOption = (payload: TreeCutCostOptionApplyPayload) => {
        const layoutKey = treeCuttingForLayoutKey ?? effectiveTreeLayoutKey ?? 'default';
        const state = useViewerStore.getState() as any;
        const aiScenarios = patchScenarioWithCutCostOption(
            [...(state.aiModuleFillingScenarios ?? [])],
            payload,
        );
        const byLayoutKey = { ...(state.aiModuleFillingTreeScenariosByLayoutKey ?? {}) } as Record<string, any[]>;
        if (Array.isArray(byLayoutKey[layoutKey])) {
            byLayoutKey[layoutKey] = patchScenarioWithCutCostOption([...byLayoutKey[layoutKey]], payload);
        }
        useViewerStore.setState({
            aiModuleFillingScenarios: aiScenarios,
            aiModuleFillingTreeScenariosByLayoutKey: byLayoutKey,
        } as any);
        setAlternateCutModal((s) => ({ ...s, open: false }));
    };

    const financingRoofCount = (layoutRc: number | undefined, roofsForRow: number | string | undefined) => {
        if (typeof layoutRc === 'number' && Number.isFinite(layoutRc)) return layoutRc;
        if (typeof roofsForRow === 'number' && Number.isFinite(roofsForRow)) return roofsForRow;
        return roofCards?.length ?? 0;
    };

    const handleOpenEpcModal = useCallback(() => {
        setEpcModalOpen(true);
    }, []);

    const handleCloseEpcModal = useCallback(() => {
        setEpcModalOpen(false);
    }, []);

    const epcModalDesigns = useMemo((): DashboardAiDesignEpcRow[] => {
        const rows: DashboardAiDesignEpcRow[] = [];
        const meta: Record<
            string,
            { scenario: { actualWattage?: number; cutCost?: unknown }; table: TreeCutPricingTable; rowIndex: number }
        > = {};
        const modulePower = Number((activeModule as any)?.power) || 0;
        const layoutMultiplier = layoutMetricsByKey?.[effectiveTreeLayoutKey]?.multiplier ?? null;
        const modulesNumeric =
            typeof modalTreeData.totalModulesBase === 'number' && Number.isFinite(modalTreeData.totalModulesBase)
                ? modalTreeData.totalModulesBase
                : undefined;

        const pushRow = (
            scenario: {
                actualWattage?: number;
                cutCost?: unknown;
                treeLabel?: string;
                treeIds?: string[];
                snapshotUrl?: string;
                layoutSnapshotUrl?: string;
                snapshotCaptureError?: string;
            },
            table: TreeCutPricingTable,
            rowIndex: number,
            label: string,
        ) => {
            const cardKey = treeCutDashboardCardKey(table, rowIndex);
            const thumb = resolveTreeCutThumb(scenario);
            const systemKw =
                modulesNumeric != null && modulePower > 0
                    ? String((modulesNumeric * modulePower) / 1000)
                    : typeof scenario.actualWattage === 'number'
                      ? String(scenario.actualWattage / 1000)
                      : '0';
            const estProd =
                typeof scenario.actualWattage === 'number' && Number.isFinite(scenario.actualWattage)
                    ? scenario.actualWattage
                    : undefined;
            if (estProd == null || estProd <= 0) return;
            if (!Number.isFinite(Number(systemKw)) || Number(systemKw) <= 0) return;
            meta[cardKey] = { scenario, table, rowIndex };
            rows.push({
                cardKey,
                label,
                snapshotUrl: thumb.url,
                estProd,
                systemKw,
                multiplier: layoutMultiplier,
            });
        };

        if (Array.isArray(modalTreeData.singleTreeWithWorthIt)) {
            (modalTreeData.singleTreeWithWorthIt as any[])
                .map((s: any, i: number) => ({ ...s, index: i }))
                .filter((s: any) => !(s.treeIds && s.treeIds.length > 1))
                .forEach((s: any) => {
                    const isBase = s.index === 0;
                    pushRow(
                        s,
                        'single',
                        s.index,
                        isBase ? 'Ideal (no cuts)' : `Cut tree ${s.treeLabel ?? s.treeId ?? s.index}`,
                    );
                });
        }

        if (Array.isArray(modalTreeData.multiTreeScenarios)) {
            (modalTreeData.multiTreeScenarios as any[]).forEach((m: any, multiIndex: number) => {
                const treeIdsLabel =
                    m.treeIds && Array.isArray(m.treeIds) ? m.treeIds.join(', ') : String(multiIndex);
                pushRow(m, 'multi', multiIndex, `Cut trees ${treeIdsLabel}`);
            });
        }

        treeEpcMetaRef.current = meta;
        return rows;
    }, [
        modalTreeData.singleTreeWithWorthIt,
        modalTreeData.multiTreeScenarios,
        modalTreeData.totalModulesBase,
        activeModule,
        layoutMetricsByKey,
        effectiveTreeLayoutKey,
        resolveTreeCutThumb,
    ]);

    const ensurePricingForCard = useCallback(
        async (cardKey: string, _estProd: number | undefined, _systemKw: string) => {
            const meta = treeEpcMetaRef.current[cardKey];
            if (!meta) return null;
            return ensureTreeCutPricingForCard(cardKey, meta.scenario, meta.table, meta.rowIndex);
        },
        [ensureTreeCutPricingForCard],
    );

    const treeTableProps: TreeScenarioTablesProps = {
        modalTreeData,
        layoutMetricsByKey,
        effectiveTreeLayoutKey,
        selectedRoofCombinationKey,
        activeLayoutRoofIds,
        activeModule,
        roofCards,
        renderTreeCutGoodLeapPurchaseOnCard,
        renderTreeCutIgsPrimaryOnCard,
        renderTreeCutLightReachTotalEpcPaymentOnCard,
        solarUtilityData,
        triggerTreeCutScenarioPartnerPricing,
        onOpenTreeScenarioFinancingPricing,
        isTreeCutScenarioSaved,
        handleSaveTreeCutScenario,
        applyingScenarioIndex,
        handleUseThisLayout,
        treeCuttingForLayoutKey,
        treeScenariosByLayoutKey,
        setTreeCuttingPopupOpen,
        setTreeCuttingForLayoutKey,
        setIsDashboardOpen,
        sceneViewer,
        openAlternateCutCosts,
        financingRoofCount,
        theme,
    };

    return (
        <>
            <TreeCuttingDefaultsModal open={treeCutDefaultsModalOpen} onClose={() => setTreeCutDefaultsModalOpen(false)} />
            <TreeCutAlternateCutCostsModal
                open={alternateCutModal.open}
                onClose={() => setAlternateCutModal((s) => ({ ...s, open: false }))}
                theme={theme === 'light' ? 'light' : 'dark'}
                sceneViewer={sceneViewer}
                treeIds={alternateCutModal.treeIds}
                scenarioLabel={alternateCutModal.label}
                scenarioIndex={alternateCutModal.scenarioIndex}
                defaultCutCostOnCard={alternateCutModal.defaultCutCost}
                selectedOptionId={alternateCutModal.selectedOptionId}
                onApplyCutOption={handleApplyCutOption}
            />
                            <div
                                className={`editor-ai-tree-cutting-panel--rail obd-theme-${theme}`}
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    className={`obd-card obd-card--saved-designs obd-ai-panel-surface editor-ai-tree-cutting-rail-card editor-ai-designs-grid editor-ai-designs-grid--catalog obd-ai-tree-saved-catalog-wrap obd-theme-${theme}`}
                                    style={{
                                        padding: '10px 12px 12px',
                                        borderRadius: 16,
                                        flex: 1,
                                        minHeight: 0,
                                    }}
                                >
                                    <div
                                        className="editor-ai-tree-cutting-drawer__body"
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                            padding: 0,
                                        }}
                                    >
                                                                    <div className="editor-ai-tree-cutting-drawer__inner">
                                <div className={`editor-ai-tree-cutting-toolbar obd-theme-${theme}`}>
                                    <div className="editor-ai-tree-cutting-toolbar__head">
                                        {designLabel ? (
                                            <h2 className="editor-ai-tree-cutting-toolbar__design-title">{designLabel}</h2>
                                        ) : null}
                                        <div
                                            className={`editor-ai-command-strip editor-ai-command-strip--dock editor-ai-command-strip--tree editor-ai-command-strip--tree-inline obd-theme-${theme}`}
                                            role="toolbar"
                                            aria-label="Tree cutting controls"
                                        >
                                            <div className="editor-ai-command-strip__trail-scroll">
                                                <div className="editor-ai-command-strip__trail">
                                            <Dropdown
                                                menu={{ items: toolbarOverflowItems }}
                                                trigger={['click']}
                                                placement="bottomRight"
                                                open={toolbarOverflowOpen}
                                                onOpenChange={setToolbarOverflowOpen}
                                                destroyOnHidden
                                            >
                                                <button
                                                    type="button"
                                                    className="editor-ai-command-strip__icon-btn"
                                                    aria-label="More tree cutting actions"
                                                    aria-haspopup="menu"
                                                    aria-expanded={toolbarOverflowOpen}
                                                >
                                                    <DotsThreeIcon size={18} weight="bold" aria-hidden />
                                                </button>
                                            </Dropdown>
                                            <div
                                                className="editor-ai-command-strip__view"
                                                role="group"
                                                aria-label="Scenario layout"
                                            >
                                                <button
                                                    type="button"
                                                    className={`editor-ai-command-strip__view-btn${treeScenariosCatalogViewMode === 'table' ? ' editor-ai-command-strip__view-btn--active' : ''}`}
                                                    onClick={() => setTreeScenariosCatalogViewMode('table')}
                                                    aria-pressed={treeScenariosCatalogViewMode === 'table'}
                                                    title="Table view"
                                                >
                                                    <ListBulletsIcon
                                                        size={16}
                                                        weight={treeScenariosCatalogViewMode === 'table' ? 'fill' : 'regular'}
                                                        aria-hidden
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`editor-ai-command-strip__view-btn${treeScenariosCatalogViewMode === 'card' ? ' editor-ai-command-strip__view-btn--active' : ''}`}
                                                    onClick={() => setTreeScenariosCatalogViewMode('card')}
                                                    aria-pressed={treeScenariosCatalogViewMode === 'card'}
                                                    title="Card view"
                                                >
                                                    <SquaresFourIcon
                                                        size={16}
                                                        weight={treeScenariosCatalogViewMode === 'card' ? 'fill' : 'regular'}
                                                        aria-hidden
                                                    />
                                                </button>
                                            </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="editor-ai-tree-cutting-toolbar__intro">
                                        {treeCuttingForLayoutKey ? (
                                            <>
                                                Showing tree cutting for{' '}
                                                <strong>this design only</strong> (selected from the card).
                                            </>
                                        ) : (
                                            <>
                                                Explore how tree cutting affects the{' '}
                                                <strong>current layout</strong> before applying it.
                                            </>
                                        )}
                                    </p>
                                </div>
                                <div className="editor-ai-tree-cutting-drawer__scroll">
                                {aiModuleFillingScenarios?.length === 1 && (
                                    <p style={{ fontSize: 12, color: 'rgba(209,213,219,0.9)', marginBottom: 12, padding: '10px 12px', background: 'rgba(15,23,42,0.9)', borderRadius: 8, border: '1px solid rgba(55,65,81,0.9)' }}>
                                        No trees have high impact. There are no tree-cutting scenarios to simulate.
                                    </p>
                                )}
                                {(Array.isArray(modalTreeData.singleTreeWithWorthIt) && modalTreeData.singleTreeWithWorthIt.length > 0) ? (
                                    <>
                                        <div className="editor-ai-tree-cutting-section-label editor-ai-tree-cutting-section-label--grid">
                                            Single-tree scenarios
                                        </div>
                                        {treeScenariosCatalogViewMode === 'card' ? (
                                        <div
                                                role="list"
                                                className={`obd-ai-catalog-list obd-ai-catalog-list--dashboard obd-ai-catalog-list--tree-scenarios obd-theme-${theme}`}
                                            >
                                                {(modalTreeData.singleTreeWithWorthIt as any[])
                                                    .map((s: any, i: number) => ({ ...s, index: i }))
                                                    .filter((s: any) => !(s.treeIds && s.treeIds.length > 1))
                                                    .map((s: any) => {
                                                        const isBase = s.treeId == null;
                                                        const isRecommended = !isBase && s.worthIt;
                                                        const gainW = modalTreeData.baseWattage
                                                            ? (s.actualWattage ?? 0) - modalTreeData.baseWattage
                                                            : 0;
                                                        const gainPct = modalTreeData.baseWattage
                                                            ? (gainW / modalTreeData.baseWattage) * 100
                                                            : 0;
                                                        const layoutRoofMetrics = layoutMetricsByKey?.[effectiveTreeLayoutKey] || {};
                                                        const layoutRoofCount =
                                                            typeof layoutRoofMetrics.roofCount === 'number' &&
                                                            Number.isFinite(layoutRoofMetrics.roofCount)
                                                                ? layoutRoofMetrics.roofCount
                                                                : effectiveTreeLayoutKey === selectedRoofCombinationKey
                                                                  ? (activeLayoutRoofIds?.length ?? undefined)
                                                                  : undefined;
                                                        const modulesForRow =
                                                            (typeof modalTreeData.totalModulesBase === 'number' &&
                                                            Number.isFinite(modalTreeData.totalModulesBase)
                                                                ? modalTreeData.totalModulesBase
                                                                : undefined) ?? '—';
                                                        const roofsForRow =
                                                            layoutRoofCount ||
                                                            roofCards?.length ||
                                                            (s.shadeWeights ?? []).length ||
                                                            '—';
                                                        const modulesNumericSingle =
                                                            typeof modulesForRow === 'number' && Number.isFinite(modulesForRow)
                                                                ? modulesForRow
                                                                : undefined;
                                                        const modulePowerSingle = (activeModule as any)?.power ?? 0;
                                                        const systemSizeForRowSingle =
                                                            modulesNumericSingle != null && modulePowerSingle > 0
                                                                ? ((modulesNumericSingle * modulePowerSingle) / 1000).toFixed(2)
                                                                : typeof s.actualWattage === 'number'
                                                                  ? (s.actualWattage / 1000).toFixed(2)
                                                                  : '—';
                                                        const cutDetailSingle = formatTreeScenarioCutDetail(s);
                                                        const layoutPerfDetail = buildDashboardLayoutPerfDetailLine(
                                                            layoutMetricsByKey?.[effectiveTreeLayoutKey],
                                                        );
                                                        const detailLineSingle = joinDashboardCatalogDetailLines(
                                                            layoutPerfDetail,
                                                            cutDetailSingle,
                                                            isBase
                                                                ? null
                                                                : `Production ${gainPct >= 0 ? '+' : ''}${gainPct.toFixed(2)}%`,
                                                        );
                                                        const isSingleSelected = isBase;
                                                        const singleThumb = resolveTreeCutThumb(s as any);
                                                        return (
                                                            <React.Fragment key={s.index}>
                                                            <DashboardAiCatalogListRow
                                                                theme={theme === 'dark' ? 'dark' : 'light'}
                                                                rowIndex={s.index}
                                                                cardKey={`tree-single-${s.index}`}
                                                                snapshotUrl={singleThumb.url}
                                                                snapshotError={singleThumb.error}
                                                                emptyThumb={isBase ? 'sparkle' : 'tree'}
                                                                label={isBase ? 'Ideal (no cuts)' : `Cut tree ${s.treeLabel}`}
                                                                summaryLine={`${modulesForRow} modules · ${roofsForRow} roofs`}
                                                                prodLine={formatDashboardAnnualProductionLine(s.actualWattage)}
                                                                detailLine={detailLineSingle || undefined}
                                                                statusLabel={
                                                                    isBase ? 'BASELINE' : isRecommended ? 'RECOMMENDED' : 'OPTION'
                                                                }
                                                                statusVariant={
                                                                    isBase || isRecommended ? 'lime' : 'neutral'
                                                                }
                                                                systemKw={systemSizeForRowSingle}
                                                                isSelected={isSingleSelected}
                                                                isSimulating={false}
                                                                selectForPricingDisabled
                                                                onSelectForPricing={() => undefined}
                                                                onPricing={() => undefined}
                                                                pricingDisabled
                                                                onOpen3D={() => {
                                                                    useViewerStore.setState({ isAiGeneratedDesign: true });
                                                                    handleUseThisLayout(
                                                                        s.index,
                                                                        treeCuttingForLayoutKey,
                                                                        treeScenariosByLayoutKey?.[effectiveTreeLayoutKey],
                                                                    );
                                                                    setTreeCuttingPopupOpen(false);
                                                                    setTreeCuttingForLayoutKey(null);
                                                                    setIsDashboardOpen(false);
                                                                }}
                                                                open3dBusy={applyingScenarioIndex === s.index}
                                                                hideDollarAction
                                                                hideChevron
                                                                variant="dashboard"
                                                                supplementsBelowThumb={supplementsBelowThumb}
                                                                showEpcAction={Boolean(solarUtilityData)}
                                                                epcBusy={false}
                                                                onOpenEpc={handleOpenEpcModal}
                                                                extraActions={
                                                                    <>
                                                                        {/* <span
                                                                            className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn${
                                                                                isBase || isRecommended
                                                                                    ? ' obd-ai-catalog-list__icon-btn--selected'
                                                                                    : ''
                                                                            }`}
                                                                            title={
                                                                                isBase
                                                                                    ? 'Baseline scenario (no cuts)'
                                                                                    : isRecommended
                                                                                      ? 'AI marked this cut as worthwhile'
                                                                                      : 'Not flagged as the top single-tree pick'
                                                                            }
                                                                        >
                                                                            <CheckSquareIcon
                                                                                size={16}
                                                                                weight={
                                                                                    isBase || isRecommended ? 'fill' : 'duotone'
                                                                                }
                                                                                aria-hidden
                                                                            />
                                                                        </span> */}
                                                                        {!isBase && s.treeId ? (
                                                                            <button
                                                                                type="button"
                                                                                className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--tree"
                                                                                disabled={!sceneViewer}
                                                                                title="Compare tree cut cost for other stump/haul options (Tree Calculator)"
                                                                                aria-label="Compare cut cost options"
                                                                                onClick={() =>
                                                                                    openAlternateCutCosts(
                                                                                        [String(s.treeId)],
                                                                                        `Cut tree ${s.treeLabel ?? s.treeId}`,
                                                                                        s.index,
                                                                                        s.cutCost != null && Number.isFinite(Number(s.cutCost))
                                                                                            ? Number(s.cutCost)
                                                                                            : null,
                                                                                        s.cutCostOptionId ?? null,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <ScalesIcon size={19} weight="regular" aria-hidden />
                                                                            </button>
                                                                        ) : null}
                                                                        <button
                                                                            type="button"
                                                                            className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--pricing"
                                                                            disabled={!solarUtilityData}
                                                                            title={
                                                                                solarUtilityData
                                                                                    ? 'Open financing pricing and load GoodLeap / LightReach for this scenario'
                                                                                    : 'Load opportunity utility data on the dashboard first'
                                                                            }
                                                                            aria-label="Open financing pricing and partner rates for this scenario"
                                                                            onClick={() => {
                                                                                void triggerTreeCutScenarioPartnerPricing(
                                                                                    s,
                                                                                    'single',
                                                                                    s.index,
                                                                                );
                                                                                onOpenTreeScenarioFinancingPricing?.(s, 'single', s.index, {
                                                                                    systemSizeKw:
                                                                                        modulesNumericSingle != null && modulePowerSingle > 0
                                                                                            ? (modulesNumericSingle * modulePowerSingle) / 1000
                                                                                            : typeof s.actualWattage === 'number'
                                                                                              ? s.actualWattage / 1000
                                                                                              : undefined,
                                                                                    systemKw:
                                                                                        systemSizeForRowSingle === '—'
                                                                                            ? typeof s.actualWattage === 'number'
                                                                                                ? (s.actualWattage / 1000).toFixed(2)
                                                                                                : '0'
                                                                                            : systemSizeForRowSingle,
                                                                                    estModules: modulesNumericSingle,
                                                                                    roofCount: financingRoofCount(layoutRoofCount, roofsForRow),
                                                                                });
                                                                            }}
                                                                        >
                                                                            <CurrencyDollarIcon
                                                                                size={19}
                                                                                weight="regular"
                                                                                aria-hidden
                                                                            />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--wishlist${
                                                                                isTreeCutScenarioSaved(s.index)
                                                                                    ? ' obd-ai-catalog-list__icon-btn--saved'
                                                                                    : ''
                                                                            }`}
                                                                            title={
                                                                                isTreeCutScenarioSaved(s.index)
                                                                                    ? 'Already in wishlist'
                                                                                    : 'Add to wishlist'
                                                                            }
                                                                            aria-label={
                                                                                isTreeCutScenarioSaved(s.index)
                                                                                    ? 'Remove from wishlist'
                                                                                    : 'Add to wishlist'
                                                                            }
                                                                            onClick={() =>
                                                                                handleSaveTreeCutScenario(s, s.index)
                                                                            }
                                                                        >
                                                                            <HeartIcon
                                                                                size={19}
                                                                                weight={
                                                                                    isTreeCutScenarioSaved(s.index)
                                                                                        ? 'fill'
                                                                                        : 'regular'
                                                                                }
                                                                                aria-hidden
                                                                            />
                                                                        </button>
                                                                    </>
                                                                }
                                                            />
                                                            </React.Fragment>
                                                        );
                                                    })}
                                            </div>
                                        ) : (
                                            <DashboardAiRailTreeCuttingSingleTable {...treeTableProps} />
                                        )}

                                        {Array.isArray(modalTreeData.multiTreeScenarios) && modalTreeData.multiTreeScenarios.length > 0 && (
                                            <>
                                                <div className="editor-ai-tree-cutting-section-label editor-ai-tree-cutting-section-label--grid editor-ai-tree-cutting-section-label--spaced">
                                                    Multi-tree cut (actual)
                                                </div>
                                                {treeScenariosCatalogViewMode === 'card' ? (
                                                <div
                                                        role="list"
                                                        className={`obd-ai-catalog-list obd-ai-catalog-list--dashboard obd-ai-catalog-list--tree-scenarios obd-theme-${theme}`}
                                                    >
                                                        {(modalTreeData.multiTreeScenarios as any[]).map((m: any, multiIndex: number) => {
                                                            const enriched =
                                                                Array.isArray(modalTreeData.singleTreeWithWorthIt) &&
                                                                typeof m.index === 'number'
                                                                    ? (modalTreeData.singleTreeWithWorthIt as any[])[m.index]
                                                                    : undefined;
                                                            const gainPct = modalTreeData.baseWattage
                                                                ? ((m.actualWattage ?? 0) - modalTreeData.baseWattage) /
                                                                  modalTreeData.baseWattage *
                                                                  100
                                                                : 0;
                                                            const isRecommended = enriched?.worthIt === true;
                                                            const modulesNumeric =
                                                                typeof modalTreeData.totalModulesBase === 'number' &&
                                                                Number.isFinite(modalTreeData.totalModulesBase)
                                                                    ? modalTreeData.totalModulesBase
                                                                    : undefined;
                                                            const modulesForRow = modulesNumeric ?? '—';
                                                            const layoutRoofMetrics = layoutMetricsByKey?.[effectiveTreeLayoutKey] || {};
                                                            const layoutRoofCount =
                                                                typeof layoutRoofMetrics.roofCount === 'number' &&
                                                                Number.isFinite(layoutRoofMetrics.roofCount)
                                                                    ? layoutRoofMetrics.roofCount
                                                                    : effectiveTreeLayoutKey === selectedRoofCombinationKey
                                                                      ? (activeLayoutRoofIds?.length ?? undefined)
                                                                      : undefined;
                                                            const roofsForRow =
                                                                layoutRoofCount ||
                                                                roofCards?.length ||
                                                                (m.shadeWeights ?? []).length ||
                                                                '—';
                                                            const modulePower = (activeModule as any)?.power ?? 0;
                                                            const systemSizeForRow =
                                                                modulesNumeric != null && modulePower > 0
                                                                    ? ((modulesNumeric * modulePower) / 1000).toFixed(2)
                                                                    : typeof m.actualWattage === 'number'
                                                                      ? (m.actualWattage / 1000).toFixed(2)
                                                                      : '—';
                                                            const treeIdsLabel =
                                                                m.treeIds && Array.isArray(m.treeIds)
                                                                    ? m.treeIds.join(', ')
                                                                    : '—';
                                                            const cutDetailMulti = formatTreeScenarioCutDetail(m);
                                                            const layoutPerfDetailMulti = buildDashboardLayoutPerfDetailLine(
                                                                layoutMetricsByKey?.[effectiveTreeLayoutKey],
                                                            );
                                                            const detailMulti = joinDashboardCatalogDetailLines(
                                                                layoutPerfDetailMulti,
                                                                cutDetailMulti,
                                                                `Production ${gainPct >= 0 ? '+' : ''}${gainPct.toFixed(2)}%`,
                                                            );
                                                            const isMultiSelected = false;
                                                            const multiThumb = resolveTreeCutThumb(m as any);
                                                            return (
                                                                <React.Fragment key={`multi-${multiIndex}`}>
                                                                <DashboardAiCatalogListRow
                                                                    theme={theme === 'dark' ? 'dark' : 'light'}
                                                                    rowIndex={multiIndex}
                                                                    cardKey={`tree-multi-${multiIndex}`}
                                                                    snapshotUrl={multiThumb.url}
                                                                    snapshotError={multiThumb.error}
                                                                    emptyThumb="tree"
                                                                    label={`Cut trees ${treeIdsLabel}`}
                                                                    summaryLine={`${modulesForRow} modules · ${roofsForRow} roofs`}
                                                                    prodLine={formatDashboardAnnualProductionLine(m.actualWattage)}
                                                                    detailLine={detailMulti || undefined}
                                                                    statusLabel={isRecommended ? 'RECOMMENDED' : 'OPTION'}
                                                                    statusVariant={isRecommended ? 'lime' : 'neutral'}
                                                                    systemKw={systemSizeForRow}
                                                                    isSelected={isMultiSelected}
                                                                    isSimulating={false}
                                                                    selectForPricingDisabled
                                                                    onSelectForPricing={() => undefined}
                                                                    onPricing={() => undefined}
                                                                    pricingDisabled
                                                                    onOpen3D={() => {
                                                                        useViewerStore.setState({ isAiGeneratedDesign: true });
                                                                        handleUseThisLayout(
                                                                            m.index,
                                                                            treeCuttingForLayoutKey,
                                                                            treeScenariosByLayoutKey?.[effectiveTreeLayoutKey],
                                                                        );
                                                                        setTreeCuttingPopupOpen(false);
                                                                        setTreeCuttingForLayoutKey(null);
                                                                        setIsDashboardOpen(false);
                                                                    }}
                                                                    open3dBusy={applyingScenarioIndex === m.index}
                                                                    hideDollarAction
                                                                    hideChevron
                                                                    variant="dashboard"
                                                                    supplementsBelowThumb={supplementsBelowThumb}
                                                                    showEpcAction={Boolean(solarUtilityData)}
                                                                    epcBusy={false}
                                                                    onOpenEpc={handleOpenEpcModal}
                                                                    extraActions={
                                                                        <> 
                                                                            {m.treeIds && Array.isArray(m.treeIds) && m.treeIds.length > 0 ? (
                                                                                <button
                                                                                    type="button"
                                                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--tree"
                                                                                    disabled={!sceneViewer}
                                                                                    title="Compare tree cut cost for other stump/haul options (Tree Calculator)"
                                                                                    aria-label="Compare cut cost options"
                                                                                    onClick={() =>
                                                                                        openAlternateCutCosts(
                                                                                            m.treeIds.map(String),
                                                                                            `Cut trees ${m.treeIds.join(', ')}`,
                                                                                            m.index,
                                                                                            m.cutCost != null && Number.isFinite(Number(m.cutCost))
                                                                                                ? Number(m.cutCost)
                                                                                                : null,
                                                                                            m.cutCostOptionId ?? null,
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <ScalesIcon size={19} weight="regular" aria-hidden />
                                                                                </button>
                                                                            ) : null}
                                                                            <button
                                                                                type="button"
                                                                                className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--pricing"
                                                                                disabled={!solarUtilityData}
                                                                                title={
                                                                                    solarUtilityData
                                                                                        ? 'Open financing pricing and load GoodLeap / LightReach for this scenario'
                                                                                        : 'Load opportunity utility data on the dashboard first'
                                                                                }
                                                                                aria-label="Open financing pricing and partner rates for this scenario"
                                                                                onClick={() => {
                                                                                    void triggerTreeCutScenarioPartnerPricing(
                                                                                        m,
                                                                                        'multi',
                                                                                        multiIndex,
                                                                                    );
                                                                                    onOpenTreeScenarioFinancingPricing?.(m, 'multi', multiIndex, {
                                                                                        systemSizeKw:
                                                                                            modulesNumeric != null && modulePower > 0
                                                                                                ? (modulesNumeric * modulePower) / 1000
                                                                                                : typeof m.actualWattage === 'number'
                                                                                                  ? m.actualWattage / 1000
                                                                                                  : undefined,
                                                                                        systemKw:
                                                                                            systemSizeForRow === '—'
                                                                                                ? typeof m.actualWattage === 'number'
                                                                                                    ? (m.actualWattage / 1000).toFixed(2)
                                                                                                    : '0'
                                                                                                : systemSizeForRow,
                                                                                        estModules: modulesNumeric,
                                                                                        roofCount: financingRoofCount(layoutRoofCount, roofsForRow),
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <CurrencyDollarIcon
                                                                                    size={19}
                                                                                    weight="regular"
                                                                                    aria-hidden
                                                                                />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--wishlist${
                                                                                    isTreeCutScenarioSaved(m.index)
                                                                                        ? ' obd-ai-catalog-list__icon-btn--saved'
                                                                                        : ''
                                                                                }`}
                                                                                title={
                                                                                    isTreeCutScenarioSaved(m.index)
                                                                                        ? 'Already in wishlist'
                                                                                        : 'Add to wishlist'
                                                                                }
                                                                                aria-label={
                                                                                    isTreeCutScenarioSaved(m.index)
                                                                                        ? 'Remove from wishlist'
                                                                                        : 'Add to wishlist'
                                                                                }
                                                                                onClick={() =>
                                                                                    handleSaveTreeCutScenario(m, m.index)
                                                                                }
                                                                            >
                                                                                <HeartIcon
                                                                                    size={19}
                                                                                    weight={
                                                                                        isTreeCutScenarioSaved(m.index)
                                                                                            ? 'fill'
                                                                                            : 'regular'
                                                                                    }
                                                                                    aria-hidden
                                                                                />
                                                                            </button>
                                                                        </>
                                                                    }
                                                                />
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <DashboardAiRailTreeCuttingMultiTable {...treeTableProps} />
                                                )}

                                            </>
                                        )}
                                    </>
                                ) : (
                                    <p style={{ color: 'rgba(156,163,175,0.9)', fontSize: 13 }}>Run AI to see tree-cutting scenarios.</p>
                                )}
                                </div>
                            </div>
                                    </div>
                                </div>
                            </div>
            <DashboardAiDesignsEpcModal
                theme={theme === 'dark' ? 'dark' : 'light'}
                open={epcModalOpen}
                onClose={handleCloseEpcModal}
                designs={epcModalDesigns}
                ensurePricingForCard={ensurePricingForCard}
                getEpcByPartner={getTreeCutEpcByPartner}
                getPartnerCellState={getTreeCutPartnerCellState}
                zIndex={OBD_AI_DESIGNS_EPC_TREE_CUTTING_NESTED_MODAL_Z}
            />
        </>
    );
}
